/**
 * Ontology Maturity Panel - Slice F (Sprint 5)
 * Displays SHACL validation results with maturity bar and violations table
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

interface OntologyValidation {
  maturity: number;
  passed: number;
  failed: number;
  total_shapes: number;
  conforms: boolean;
  violations: Array<{
    shape: string;
    focusNode: string;
    path: string;
    message: string[];
    severity: string;
  }>;
  provenance: {
    ontology_sha256: string;
    shapes_sha256: string;
    validator_version: string;
    validated_at: string;
  };
}

export const OntologyMaturityPanel: React.FC = () => {
  const { job_id } = useParams<{ job_id: string }>();
  const [validation, setValidation] = useState<OntologyValidation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchValidation = async () => {
      try {
        const response = await fetch(`/analysis/${job_id}/ontology-validation`, {
          headers: { 'Accept': 'application/json' }
        });

        if (!response.ok) {
          if (response.status === 404) {
            const errorData = await response.json();
            setError(errorData.hint || 'Ontology validation not available');
          } else {
            setError('Failed to load ontology validation');
          }
          setLoading(false);
          return;
        }

        const data = await response.json();
        setValidation(data);
        setLoading(false);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load ontology validation');
        setLoading(false);
      }
    };

    if (job_id) {
      fetchValidation();
    }
  }, [job_id]);

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading ontology validation results...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '20px',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '4px',
        margin: '20px'
      }}>
        <h3>Ontology Validation Not Available</h3>
        <p>{error}</p>
        <p style={{ fontSize: '0.9em', color: '#666' }}>
          Set <code>ONTOLOGY_VALIDATION=true</code> in your environment to enable this feature.
        </p>
      </div>
    );
  }

  if (!validation) {
    return null;
  }

  // Calculate maturity percentage
  const maturityPercent = Math.round(validation.maturity * 100);

  // Determine bar color based on maturity
  const getBarColor = (maturity: number): string => {
    if (maturity >= 0.8) return '#28a745'; // Green
    if (maturity >= 0.5) return '#ffc107'; // Yellow
    return '#dc3545'; // Red
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <h2 style={{ marginBottom: '10px' }}>Ontology Maturity</h2>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        SHACL validation results for DATA Compass ontology v3
      </p>

      {/* Maturity Bar */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '8px'
        }}>
          <span style={{ fontWeight: 'bold' }}>Overall Maturity</span>
          <span style={{ fontWeight: 'bold', fontSize: '1.2em' }}>
            {maturityPercent}%
          </span>
        </div>

        {/* Progress Bar */}
        <div style={{
          width: '100%',
          height: '30px',
          backgroundColor: '#e9ecef',
          borderRadius: '4px',
          overflow: 'hidden',
          border: '1px solid #dee2e6'
        }}>
          <div style={{
            width: `${maturityPercent}%`,
            height: '100%',
            backgroundColor: getBarColor(validation.maturity),
            transition: 'width 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingRight: '10px',
            color: 'white',
            fontWeight: 'bold'
          }}>
            {maturityPercent > 10 && `${validation.passed}/${validation.total_shapes} passed`}
          </div>
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '15px',
          marginTop: '20px'
        }}>
          <div style={{
            padding: '15px',
            backgroundColor: '#d4edda',
            borderRadius: '4px',
            border: '1px solid #c3e6cb'
          }}>
            <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#155724' }}>
              {validation.passed}
            </div>
            <div style={{ color: '#155724' }}>Shapes Passed</div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: '#f8d7da',
            borderRadius: '4px',
            border: '1px solid #f5c6cb'
          }}>
            <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#721c24' }}>
              {validation.failed}
            </div>
            <div style={{ color: '#721c24' }}>Shapes Failed</div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: '#d1ecf1',
            borderRadius: '4px',
            border: '1px solid #bee5eb'
          }}>
            <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#0c5460' }}>
              {validation.total_shapes}
            </div>
            <div style={{ color: '#0c5460' }}>Total Shapes</div>
          </div>
        </div>
      </div>

      {/* Violations Table */}
      {validation.violations.length > 0 && (
        <div style={{ marginBottom: '40px' }}>
          <h3 style={{ marginBottom: '15px' }}>
            Violations ({validation.violations.length})
          </h3>

          <div style={{
            overflowX: 'auto',
            border: '1px solid #dee2e6',
            borderRadius: '4px'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              backgroundColor: 'white'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={tableHeaderStyle}>Shape</th>
                  <th style={tableHeaderStyle}>Focus Node</th>
                  <th style={tableHeaderStyle}>Path</th>
                  <th style={tableHeaderStyle}>Message</th>
                  <th style={tableHeaderStyle}>Severity</th>
                </tr>
              </thead>
              <tbody>
                {validation.violations.map((violation, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: '1px solid #dee2e6',
                      backgroundColor: idx % 2 === 0 ? 'white' : '#f8f9fa'
                    }}
                  >
                    <td style={tableCellStyle}>
                      <code style={{
                        fontSize: '0.9em',
                        padding: '2px 6px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '3px'
                      }}>
                        {violation.shape}
                      </code>
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{
                        maxWidth: '300px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '0.85em',
                        color: '#666'
                      }}>
                        {violation.focusNode}
                      </div>
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{
                        maxWidth: '250px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '0.85em',
                        color: '#666'
                      }}>
                        {violation.path}
                      </div>
                    </td>
                    <td style={tableCellStyle}>
                      {Array.isArray(violation.message)
                        ? violation.message.join(', ')
                        : violation.message
                      }
                    </td>
                    <td style={tableCellStyle}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '3px',
                        fontSize: '0.85em',
                        fontWeight: 'bold',
                        backgroundColor: violation.severity === 'Violation' ? '#f8d7da' : '#fff3cd',
                        color: violation.severity === 'Violation' ? '#721c24' : '#856404'
                      }}>
                        {violation.severity}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Conformance Status */}
      {validation.violations.length === 0 && (
        <div style={{
          padding: '20px',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          marginBottom: '40px'
        }}>
          <h3 style={{ color: '#155724', marginTop: 0 }}>
            ✓ All Shapes Passed
          </h3>
          <p style={{ color: '#155724', marginBottom: 0 }}>
            The dataset conforms to all {validation.total_shapes} SHACL shapes in the ontology.
          </p>
        </div>
      )}

      {/* Provenance Footer */}
      <div style={{
        marginTop: '40px',
        padding: '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        fontSize: '0.85em',
        color: '#666'
      }}>
        <h4 style={{ marginTop: 0, marginBottom: '10px', color: '#333' }}>Provenance</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <strong>Ontology SHA-256:</strong>{' '}
            <code>{validation.provenance.ontology_sha256}</code>
          </div>
          <div>
            <strong>Shapes SHA-256:</strong>{' '}
            <code>{validation.provenance.shapes_sha256}</code>
          </div>
          <div>
            <strong>Validator:</strong>{' '}
            {validation.provenance.validator_version}
          </div>
          <div>
            <strong>Validated:</strong>{' '}
            {new Date(validation.provenance.validated_at).toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles
const tableHeaderStyle: React.CSSProperties = {
  padding: '12px 15px',
  textAlign: 'left',
  fontWeight: 'bold',
  borderBottom: '2px solid #dee2e6'
};

const tableCellStyle: React.CSSProperties = {
  padding: '12px 15px',
  verticalAlign: 'top'
};

export default OntologyMaturityPanel;
